import { useState, FormEvent, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { confirmSignUp, resendCode, signIn, Kid } from '../auth/cognito';
import { createProfile, Role } from '../api/profile';
import { useAuth } from '../auth/AuthContext';

type PendingProfile = {
  role: Role;
  parentName: string;
  phoneNumber: string;
  kids: Kid[];
  password: string;
  email: string;
};

export default function VerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const stateEmail = (location.state as { email?: string } | null)?.email ?? '';
  const [email, setEmail] = useState(stateEmail);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // If the user lands here directly, try to recover the email from the stash.
    if (!email) {
      const raw = sessionStorage.getItem('mk_pending_profile');
      if (raw) {
        try { setEmail(JSON.parse(raw).email); } catch { /* ignore */ }
      }
    }
  }, [email]);

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await confirmSignUp(email, code.trim());

      // Auto-sign-in with the password stashed at registration, then POST
      // the parent profile (kids + phone) to DynamoDB via the API.
      const raw = sessionStorage.getItem('mk_pending_profile');
      if (raw) {
        const pending: PendingProfile = JSON.parse(raw);
        const session = await signIn(email, pending.password);
        const idToken = session.getIdToken().getJwtToken();
        try {
          await createProfile(idToken, {
            role: pending.role ?? 'parent',
            parentName: pending.parentName,
            phoneNumber: pending.phoneNumber,
            kids: pending.kids ?? [],
          });
        } catch (apiErr) {
          // Non-fatal — the user is signed up. They'll see an empty
          // dashboard and can fill kid info later. Log + carry on.
          // eslint-disable-next-line no-console
          console.warn('Profile create failed (will retry later):', apiErr);
        }
        sessionStorage.removeItem('mk_pending_profile');
        await refresh();
        navigate('/dashboard', { replace: true });
      } else {
        // No stash (e.g. verified later in a different tab) → just go to login.
        navigate('/login', { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    setError(null); setInfo(null);
    try {
      await resendCode(email);
      setInfo('A new code is on its way.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend code');
    }
  }

  return (
    <div className="auth-wrap">
      <form className="auth-card" onSubmit={handleVerify} noValidate>
        <h1>Verify your email</h1>
        <p className="lede">
          We sent a 6-digit code to <strong>{email || 'your email'}</strong>. Enter it below to
          finish your registration.
        </p>

        {error && <div className="error-banner">{error}</div>}
        {info && <div className="info-banner">{info}</div>}

        {!stateEmail && (
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email" type="email" required autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        )}

        <div className="field">
          <label htmlFor="code">Verification code</label>
          <input
            id="code" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required
            placeholder="123456"
            value={code} onChange={(e) => setCode(e.target.value)}
          />
        </div>

        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'Verifying…' : 'Verify and continue'}
        </button>

        <div className="switch">
          Didn't get it? Check your spam / junk folder for an email from{' '}
          <strong>no-reply@maximized.kids</strong>, or{' '}
          <button
            type="button"
            onClick={handleResend}
            style={{ background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', font: 'inherit' }}
          >
            resend the code
          </button>
          .
        </div>
      </form>
    </div>
  );
}
